<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGarageStore } from '@/stores/garage.js'
import { useRequestAQuoteStore } from '@/stores/request_a_quote.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useModalStore } from '@/stores/modal.js'
import { useDebounceFn } from '@vueuse/core'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faClose } from '@fortawesome/free-solid-svg-icons'

import Stepper_card from '@/views/request_a_quote/components/stepper_card.vue'
import Step_1 from '@/views/request_a_quote/components/step_1.vue'
import Step_2 from '@/views/request_a_quote/components/step_2.vue'
import Step_3 from '@/views/request_a_quote/components/step_3.vue'
import Step_success from '@/views/request_a_quote/components/step_success.vue'

import CheckCircle from '@/assets/icons/check_circle_24dp_FILL1_wght400_GRAD0_opsz24.svg'
import BuildCircle from '@/assets/icons/build_circle_24dp_FILL1_wght400_GRAD0_opsz24.svg'
import AssistantNavigation from '@/assets/icons/assistant_navigation_24dp_FILL1_wght400_GRAD0_opsz24.svg'
import AddCircle from '@/assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg'

import LocationBackground from '@/assets/images/location box.jpg'
import VehicleBackground from '@/assets/images/vehicle box.jpg'
import Servicebackground from '@/assets/images/service box.jpg'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'

// I18n
const { t, tm } = useI18n()

// Stores
const router = useRouter()
const route = useRoute()
const garageStore = useGarageStore()
const requestAQuoteStore = useRequestAQuoteStore()
const authenticationStore = useAuthenticationStore()
const modalStore = useModalStore()

// Variables
const isLoading = ref(true)
const previousStep = ref(1)
const transitionName = ref('slide-right')
const contentRef = ref(null)
const countdown = ref(30)
let countdownInterval = null

const currentStep = computed(() => {
  const step = Number(route.params.step)
  if (step !== previousStep.value) {
    transitionName.value = step > previousStep.value ? 'slide-left' : 'slide-right'
    previousStep.value = step
  }
  requestAQuoteStore.setCurrentStep(step)
  return step
})

const quoteId = computed(() => Number(route.query.quoteId))

const remainingQuotes = computed(() => {
  if (!authenticationStore.currentUser?.user?.attributes) return 0
  const attrs = authenticationStore.currentUser.user.attributes
  return (attrs.additional_quota || 0) + (attrs.monthly_quota || 0) + (attrs.yearly_quota || 0)
})

const selectedServiceNames = computed(() => {
  return requestAQuoteStore.selectedServices
    .map(x => x.category?.name)
    .filter(Boolean)
})

const hasSelectedServices = computed(() => selectedServiceNames.value.length > 0)
const serviceDisplayText = computed(() => 
  hasSelectedServices.value ? selectedServiceNames.value.join(', ') : t('request_a_quote.select_service')
)

// Service Provider Query
const serviceProviderUrl = computed(() => 
  requestAQuoteStore.requestedServiceProviderId 
    ? `${import.meta.env.VITE_BACKEND_URL}/api/v2/service_providers/${requestAQuoteStore.requestedServiceProviderId}`
    : null
)

const {
  isFetching: isServiceProviderLoading,
  data: serviceProviderData
} = useIntelichekFetch(serviceProviderUrl, { refetch: true, immediate: false }).json()

// Event Handlers
onMounted(async () => {
  if (!authenticationStore.currentUser) {
    router.replace({ name: 'LandingPage' })
    modalStore.setModal(modalStore.modalTypeConstants.LOGIN)
    return
  }

  // If not on step 1, redirect to step 1 to reset the process
  if (Number(route.params.step) !== 1) {
    requestAQuoteStore.clearPersistedState()
    router.replace({ name: 'RequestAQuote', params: { step: 1 } })
    return
  }

  // Always reset store state on initial load to step 1
  requestAQuoteStore.$reset()

  if (route.query.requested_service_provider_id) {
    requestAQuoteStore.setServiceProvider(route.query.requested_service_provider_id)
  }

  router.replace({ query: null })

  try {
    await garageStore.getMyGarage()
  } finally {
    isLoading.value = false
  }
})

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})


watch(() => modalStore.positiveHandleTrigger, async (value) => {
  if (value) {
    if (modalStore.modalType === modalStore.modalTypeConstants.CONFIRM_CLEAR_REQUESTED_SERVICE_PROVIDER) {
      requestAQuoteStore.setServiceProvider(null)
      modalStore.resetModal()
    }
  }
})

const clearCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

const startCountdown = () => {
  clearCountdown()
  countdown.value = 30
  countdownInterval = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearCountdown()
      router.push({ path: `/my_garage/quote/${quoteId.value}` })
    }
  }, 1000)
}

watch(() => currentStep.value, () => {
  setTimeout(() => {
    if (contentRef.value) {
      const targetPosition = contentRef.value.offsetTop - 20
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      })
    }
  }, 400)
  
  if (currentStep.value === 4 && quoteId.value) {
    startCountdown()
  } else {
    clearCountdown()
  }
})

const submitQuote = useDebounceFn(() => {
  requestAQuoteStore.submitQuote()
}, 1000, { maxWait: 500 })

// Methods

</script>

<template>
  <div class="flex flex-col lg:px-8 lg:py-4 grow">
    <div v-if="remainingQuotes <= 0" class="flex justify-center no-scrollbar">
      <div class="bg-blue-500 text-white p-2 rounded-md mb-4">
        <a href="/settings/orders" class="text-white hover:text-gray-300">{{ t('request_a_quote.buy_more_quotes_link') }}</a>
      </div>
    </div>

    <div class="flex flex-col items-center w-full overflow-x-auto no-scrollbar">
      <div class="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-center lg:space-y-0 lg:space-x-2 w-full my-8">
        <stepper_card
          :value="!!garageStore.currentVehicle && garageStore.currentVehicle.id ? garageStore.currentVehicle.full_vehicle_name : t('request_a_quote.select_vehicle')"
          :type="t('request_a_quote.vehicle')"
          :icon="BuildCircle"
          :step="requestAQuoteStore.getStepStatus(1, currentStep)"
          :has-value="requestAQuoteStore.isStep1Valid"
          :background="VehicleBackground"
          :class="{ 'cursor-pointer': currentStep !== 4 }"
          @click="currentStep !== 4 ? router.push({name: 'RequestAQuote', params: {step: 1}}) : null"
        />

        <stepper_card
          :value="serviceDisplayText"
          :type="t('request_a_quote.service')"
          :icon="BuildCircle"
          :step="requestAQuoteStore.getStepStatus(2, currentStep)"
          :has-value="requestAQuoteStore.isStep2Valid"
          :background="Servicebackground"
          :class="{ 'cursor-pointer': garageStore.currentVehicle !== null && currentStep !== 4 }"
          @click="(garageStore.currentVehicle !== null) && currentStep !== 4 ? router.push({name: 'RequestAQuote', params: {step: 2}}) : null"
        />

        <stepper_card
          :value="requestAQuoteStore.selectedLocation ? `${requestAQuoteStore.selectedLocation.location_name} - ${requestAQuoteStore.selectedMileRadius} mi` : t('request_a_quote.select_location')"
          :type="t('request_a_quote.location')"
          :icon="AssistantNavigation"
          :step="requestAQuoteStore.getStepStatus(3, currentStep)"
          :has-value="requestAQuoteStore.isStep3Valid"
          :background="LocationBackground"
          :class="{ 'cursor-pointer': requestAQuoteStore.canSubmitQuoteSelectedServiceValid && currentStep !== 4 }"
          @click="(requestAQuoteStore.canSubmitQuoteSelectedServiceValid) && currentStep !== 4 ? router.push({name: 'RequestAQuote', params: {step: 3}}) : null"
        />

        <stepper_card
          :value="t('request_a_quote.complete_quote')"
          :type="t('request_a_quote.submit')"
          :icon="AddCircle"
          :step="requestAQuoteStore.getStepStatus(4, currentStep)"
          :has-value="requestAQuoteStore.isStep4Valid"
          :background="LocationBackground"
          :class="{ 'cursor-pointer': requestAQuoteStore.canSubmitQuote && currentStep !== 4 }"
          @click="requestAQuoteStore.canSubmitQuote && currentStep !== 4 ? submitQuote() : null"
        />
      </div>
    </div>
    <hr class="mb-6" />

    <div ref="contentRef" class="px-4 mb-4 relative overflow-hidden">
      <Transition :name="transitionName" mode="out-in">
        <!-- Step 1 - Vehicle Select -->
        <div v-if="currentStep === 1" key="step-1">
          <Step_1 />
        </div>

        <!-- Step 2 - Service Select -->
        <div v-else-if="currentStep === 2" key="step-2">
          <Step_2 />
        </div>

        <!-- Step 3 - Location Select -->
        <div v-else-if="currentStep === 3" key="step-3">
          <Step_3 />
        </div>

        <!-- Step 4 - Success -->
        <div v-else-if="currentStep === 4" key="step-4">
          <Step_success />
        </div>
      </Transition>



      <div class="mt-4 mb-20">
        <p v-for="blurb in tm('request_a_quote.quote_disclaimer')" class="text-xs mt-1 text-gray-500">
          {{ blurb }}
        </p>
      </div>
    </div>

    <!-- Floating Action Buttons -->
    <div class="fixed bottom-4 left-4 right-4 flex justify-between items-center z-50">
      <!-- Back Button -->
      <button v-if="currentStep > 1 && currentStep < 4"
              class="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-3 flex items-center space-x-2 shadow-lg transition-colors"
              @click="router.push({name: 'RequestAQuote', params: {step: currentStep - 1}})">
        <img src="/src/assets/icons/chevron_right_24dp_FILL1_wght400_GRAD0_opsz24.svg" alt="Back" class="rotate-180 w-5 h-5">
        <span class="text-sm font-medium">{{ t('request_a_quote.back') }}</span>
      </button>
      <div v-else></div>

      <!-- Next/Submit Button -->
      <button v-if="currentStep < 2"
              class="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-3 flex items-center space-x-2 shadow-lg transition-colors"
              :class="{'opacity-50': garageStore.currentVehicle == null}"
              :disabled="garageStore.currentVehicle == null"
              @click="(garageStore.currentVehicle !== null) ? router.push({name: 'RequestAQuote', params: {step: currentStep + 1}}) : null">
        <span class="text-sm font-medium">{{ t('request_a_quote.next') }}</span>
        <img src="/src/assets/icons/chevron_right_24dp_FILL1_wght400_GRAD0_opsz24.svg" alt="Next" class="w-5 h-5">
      </button>

      <button v-else-if="currentStep === 2"
              class="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-3 flex items-center space-x-2 shadow-lg transition-colors"
              :class="{'opacity-50': !requestAQuoteStore.canSubmitQuoteSelectedServiceValid}"
              :disabled="!requestAQuoteStore.canSubmitQuoteSelectedServiceValid"
              @click="(requestAQuoteStore.canSubmitQuoteSelectedServiceValid) ? router.push({name: 'RequestAQuote', params: {step: currentStep + 1}}) : null">
        <span class="text-sm font-medium">{{ t('request_a_quote.next') }}</span>
        <img src="/src/assets/icons/chevron_right_24dp_FILL1_wght400_GRAD0_opsz24.svg" alt="Next" class="w-5 h-5">
      </button>

      <button v-else-if="currentStep === 3"
              class="bg-green-600 hover:bg-green-700 text-white rounded-full px-4 py-3 flex items-center space-x-2 shadow-lg transition-colors"
              :class="{'opacity-50': !requestAQuoteStore.canSubmitQuote}"
              :disabled="!requestAQuoteStore.canSubmitQuote"
              @click="submitQuote">
        <span class="text-sm font-medium">{{ t('request_a_quote.submit') }}</span>
        <img src="/src/assets/icons/check_circle_24dp_FILL1_wght400_GRAD0_opsz24.svg" alt="Submit" class="w-5 h-5">
      </button>

      <button v-else-if="currentStep === 4 && quoteId"
              class="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-3 flex items-center space-x-2 shadow-lg transition-colors"
              @click="router.push({ path: `/my_garage/quote/${quoteId}` })">
        <span class="text-sm font-medium">{{ t('request_a_quote.go_to_quote') }} ({{ countdown }})</span>
        <img src="/src/assets/icons/chevron_right_24dp_FILL1_wght400_GRAD0_opsz24.svg" alt="Go to Quote" class="w-5 h-5">
      </button>
      <div v-else></div>
    </div>

    <!-- Desktop Service Provider Info -->
    <div v-if="requestAQuoteStore.requestedServiceProviderId" class="hidden lg:block fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-lg z-40">
      <div class="flex items-center gap-2">
        <p class="text-sm text-gray-600">{{ t('request_a_quote.requested_service_provider') }} {{ serviceProviderData?.data.attributes.name }}</p>
        <button class="text-gray-400 hover:text-gray-600" title="Click to Remove"
                @click="modalStore.setModal(modalStore.modalTypeConstants.CONFIRM_CLEAR_REQUESTED_SERVICE_PROVIDER)">
          <FontAwesomeIcon :icon="faClose" size="sm"></FontAwesomeIcon>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/request_a_quote/request_a_quote';

html {
  scroll-behavior: smooth;
}
</style>
